import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { Skeleton } from '../../../components/ui/Skeleton';
import {
  getOperatorNotificationPreferences,
  updateOperatorNotificationPreferences,
} from '../../../api/General/NotificationModules';
import type { OperatorNotificationPreferenceView } from '../../../api/General/NotificationModules';
import type { ChannelPreference } from '../../../api/General/NotificationPreference';
import { cadenceLabel, typeLabel } from '../../../components/notifications/notificationModuleCopy';

type Channel = 'inApp' | 'push' | 'email';
const CHANNELS: { key: Channel; label: string }[] = [
  { key: 'inApp', label: 'In-App' },
  { key: 'push', label: 'Push' },
  { key: 'email', label: 'Email' },
];

/**
 * CO-375 / E13 — owner decision 2026-09-28 (Q5): the Operator's OWN platform notification settings.
 *
 * <p>🔴 Operator-only (route guard + server Policy="Operator"). Lists only Platform-class alerts
 * (subscription renewal due / overdue, upgrade requests, tenant subscription activity) and stores only the
 * signed-in Operator's own choices. It shows no company and no company data (CLAUDE.md Role Model).</p>
 *
 * <p>Each switch applies immediately (one independent choice per row, like the Modules tab), so there is
 * no page-wide save bar. In-app off locks push off, the same rule as company users.</p>
 */
const OperatorPlatformNotifications: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<OperatorNotificationPreferenceView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [rowError, setRowError] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await getOperatorNotificationPreferences();
      if (res.errors?.length || !res.payload) setLoadError(true);
      else setRows(res.payload);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (row: OperatorNotificationPreferenceView, channel: Channel, value: boolean) => {
    const pick = (c: Channel, cp: ChannelPreference) => (c === channel ? value : cp.source === 'user' ? cp.enabled : null);
    setBusy(row.notificationTypeId);
    setRowError(null);
    try {
      const res = await updateOperatorNotificationPreferences([
        {
          notificationTypeId: row.notificationTypeId,
          emailEnabled: pick('email', row.email),
          pushEnabled: pick('push', row.push),
          inAppEnabled: pick('inApp', row.inApp),
        },
      ]);
      if (res.errors?.length) {
        setRowError(row.notificationTypeId);
        return;
      }
      const fresh = await getOperatorNotificationPreferences();
      if (!fresh.errors?.length && fresh.payload) setRows(fresh.payload);
    } catch {
      setRowError(row.notificationTypeId);
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageLayout
      title={t('Platform Notifications')}
      description={t('notificationModules.operator.description')}
      icon={BellRing}
      accentColor="slate"
    >
      {loading && !rows ? (
        <div className="divide-y divide-border rounded-lg border border-border" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-4 px-4 py-4">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-6 w-40" />
            </div>
          ))}
        </div>
      ) : loadError || !rows ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <p className="flex items-center gap-2 text-destructive">
            <AppIcon name="AlertCircle" size={16} className="shrink-0" />
            {t('Failed to load settings')}
          </p>
          <Button variant="outline" size="sm" iconName="RefreshCw" onClick={() => void load()}>
            {t('Retry')}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
          {t('notificationModules.operator.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border" data-testid="e13-operator-platform-list">
          <table className="w-full min-w-[520px] text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 text-start font-medium">{t('Notification')}</th>
                {CHANNELS.map((c) => (
                  <th key={c.key} scope="col" className="w-20 px-2 py-3 text-center font-medium">
                    {t(c.label)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row) => (
                <tr key={row.notificationTypeId} data-testid={`e13-operator-row-${row.notificationTypeId}`}>
                  <td className="px-4 py-3">
                    <div className="text-foreground">{typeLabel(t, row.notificationTypeName)}</div>
                    <div className="text-xs text-muted-foreground">{cadenceLabel(t, row.cadence)}</div>
                    {rowError === row.notificationTypeId && (
                      <p role="alert" className="mt-1 text-xs text-destructive">{t('notificationModules.prompt.error')}</p>
                    )}
                  </td>
                  {CHANNELS.map((c) => {
                    const cp = row[c.key];
                    const used = row.channels.includes(c.key);
                    return (
                      <td key={c.key} className="px-2 py-3 text-center">
                        {used ? (
                          <span className="inline-flex items-center gap-1">
                            <Switch
                              checked={cp.enabled && !cp.locked}
                              onCheckedChange={(v) => void toggle(row, c.key, v)}
                              disabled={busy !== null || cp.locked}
                              aria-label={`${typeLabel(t, row.notificationTypeName)}: ${t(c.label)}`}
                              data-testid={`e13-operator-switch-${row.notificationTypeId}-${c.key}`}
                            />
                            {cp.locked && (
                              <AppIcon
                                name="Lock"
                                size={14}
                                className="text-muted-foreground"
                                aria-label={t('notificationModules.lock.inApp')}
                              />
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground" aria-label={t('notificationModules.operator.notSent')}>
                            {'–'}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageLayout>
  );
};

export default OperatorPlatformNotifications;
